import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Car, Shirt, Bed, Users, UserX, Gift, CalendarCheck, CloudSun, ExternalLink, Copy, Check, ParkingSquare } from "lucide-react";
import { INFO_BLOCKS, WEDDING } from "@/lib/constants";
import EditableText from "@/components/EditableText";

const iconMap = { MapPin, Car, ParkingSquare, Shirt, Bed, Users, UserX, Gift, CalendarCheck, CloudSun };

export default function InfoUtili() {
  const [copiedIban, setCopiedIban] = useState(false);
  const logistica = INFO_BLOCKS.slice(0, 4);
  const domande = INFO_BLOCKS.slice(4);

  const copyIban = (iban) => {
    navigator.clipboard.writeText(iban);
    setCopiedIban(true);
    setTimeout(() => setCopiedIban(false), 2000);
  };

  const renderBlock = (block, i) => (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.05 }}
      className="border border-w-sand bg-white/40 p-6 md:p-7 hover:border-w-sage/50 transition-colors duration-300 group"
      data-testid={`info-block-${i}`}
    >
      <div className="flex items-start gap-4">
        {(() => {
          const Icon = iconMap[block.icon] || MapPin;
          return <Icon className="w-5 h-5 text-w-sage mt-0.5 shrink-0 group-hover:text-w-gold transition-colors duration-300" strokeWidth={1.5} />;
        })()}
        <div>
          <EditableText id={`info-title-${i}`} tag="h3" className="font-heading text-lg text-w-charcoal">
            {block.title}
          </EditableText>
          <p 
            className="font-body text-sm text-w-muted mt-1.5 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: block.content }}
          />
          {block.maps && (
            <a
              href={block.maps}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-body text-xs text-w-sage hover:text-w-gold mt-2 transition-colors"
              data-testid={`maps-link-${i}`}
            >
              <ExternalLink className="w-3 h-3" />
              Apri in Google Maps
            </a>
          )}
          {block.hasIban && (
            <button
              onClick={() => copyIban(block.iban)}
              className="inline-flex items-center gap-1 font-body text-xs text-w-sage hover:text-w-gold mt-2 transition-colors"
              data-testid={`copy-iban-${i}`}
            >
              {copiedIban ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copiedIban ? "IBAN copiato!" : "Copia l'IBAN"}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <section id="info" className="section-padding !pt-0 md:!pt-0 bg-w-cream" data-testid="info-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-w-sage">Le cose pratiche</p>
          <EditableText id="info-section-title" tag="h2" className="font-heading text-4xl sm:text-5xl text-w-charcoal mt-2">
            Info Utili
          </EditableText>
          <EditableText id="info-section-subtitle" tag="p" className="font-body text-w-muted mt-3 text-sm md:text-base max-w-md">
            Tutto quello che vi serve per arrivare preparati. O almeno provarci.
          </EditableText>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {logistica.map(renderBlock)}
        </div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 border border-w-sand overflow-hidden"
        >
          <iframe
            title="Mappa Borgio Verezzi"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11568.54!2d8.308!3d44.16!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12d4973d3e9e2c5f%3A0x8e0ae89e60fa3e1f!2sBorgio%20Verezzi%20SV!5e0!3m2!1sen!2sit!4v1700000000000!5m2!1sen!2sit"
            className="w-full h-[280px] md:h-[360px] border-0"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="map-embed"
          />
        </motion.div>

        {/* Domande Frequenti sub-section */}
        <div id="domande" className="mt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-w-sage">Le risposte</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-w-charcoal mt-2">Domande Frequenti</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {domande.map((block, i) => renderBlock(block, i + 5))}
          </div>
        </div>
      </div>
    </section>
  );
}
